'use client';

import Switch from '@mui/material/Switch';

import { Tooltip } from '../atomic-atoms/Tooltip';

import styles from './ToggleSwitch.module.scss';

import type { ReactNode } from 'react';

type ToggleSwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  name?: string;
  ariaLabel?: string;
  tooltip?: ReactNode;
};

export function ToggleSwitch({
  checked,
  onChange,
  disabled = false,
  label,
  helperText,
  name,
  ariaLabel,
  tooltip,
}: ToggleSwitchProps) {
  const control = (
    <Switch
      checked={checked}
      disabled={disabled}
      inputProps={{
        'aria-label': ariaLabel ?? label ?? name ?? 'Toggle switch',
        name,
      }}
      slotProps={{
        input: {
          'aria-label': ariaLabel ?? label ?? name ?? 'Toggle switch',
          name,
        },
      }}
      onChange={(_event, value) => {
        if (disabled) {
          return;
        }
        onChange(value);
      }}
      sx={{
        '& .MuiSwitch-thumb': {
          backgroundColor: 'var(--nn-accent-gray)',
        },
        '& .MuiSwitch-track': {
          backgroundColor: 'var(--nn-color-input-border)',
          opacity: 0.7,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: 'var(--nn-color-primary)',
        },
        '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
          backgroundColor: 'var(--nn-color-primary)',
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: 'var(--nn-color-primary-dark)',
          opacity: 1,
        },
      }}
    />
  );

  return (
    <div className={styles.toggleSwitch}>
      {label ? <p className={styles.toggleSwitchLabel}>{label}</p> : null}
      {helperText ? (
        <p className={styles.toggleSwitchHelper}>{helperText}</p>
      ) : null}
      {tooltip ? <Tooltip title={tooltip}>{control}</Tooltip> : control}
    </div>
  );
}
